import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ArrowLeftIcon, PencilIcon, Trash2Icon, XIcon } from "lucide-react"
import { RestoreIcon } from "@/components/icons/RestoreIcon"
import { cn } from "@/lib/utils"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

interface UnitDetail {
  id: string
  code: string
  name: string
  sgtCodes: string[] | null
  unitTypeId: string
  unitTypeCode: string
  unitTypeName: string
  licensePlate: string | null
  vin: string | null
  brand: string | null
  engineNumber: string | null
  manufactureYear: number | null
  fuelType: "diesel" | "gnc" | null
  deletedAt: string | null
  createdAt: string
  updatedAt: string
}

function fmtDate(v: string) {
  return new Date(v).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

function Field({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-sm" : "text-sm"}>
        {value ?? <span className="text-muted-foreground/50">—</span>}
      </dd>
    </div>
  )
}

export default function UnitDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { can } = usePermissions()

  const [editOpen,   setEditOpen]   = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [form,       setForm]       = useState({ code: "", name: "", licensePlate: "", brand: "", manufactureYear: "", fuelType: "" as "diesel" | "gnc" | "" })
  const [sgtCodes,   setSgtCodes]   = useState<string[]>([])
  const [sgtInput,   setSgtInput]   = useState("")
  const [formError,  setFormError]  = useState<string | null>(null)

  // ── Query ─────────────────────────────────────────────────────────────────

  const { data: unit, isLoading, isError } = useQuery({
    queryKey: ["unit", id],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/units/${id}`)
      if (!res.ok) throw new Error("Unidad no encontrada")
      return res.json() as Promise<UnitDetail>
    },
    enabled: !!id,
  })

  // ── Mutations ─────────────────────────────────────────────────────────────

  const updateMut = useMutation({
    mutationFn: async (data: {
      code: string
      name: string
      sgtCodes: string[] | null
      licensePlate: string | null
      brand: string | null
      manufactureYear: number | null
      fuelType: "diesel" | "gnc" | null
    }) => {
      const res = await authorizedFetch(`${API_URL}/api/units/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al actualizar")
      }
      return res.json() as Promise<UnitDetail>
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["unit", id] })
      qc.invalidateQueries({ queryKey: ["units"] })
      setEditOpen(false)
      setFormError(null)
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: async () => {
      await authorizedFetch(`${API_URL}/api/units/${id}`, { method: "DELETE" })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["unit", id] })
      qc.invalidateQueries({ queryKey: ["units"] })
      setDeleteOpen(false)
      navigate("/units")
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const restoreMut = useMutation({
    mutationFn: async () => {
      await authorizedFetch(`${API_URL}/api/units/${id}/restore`, { method: "PATCH" })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["unit", id] })
      qc.invalidateQueries({ queryKey: ["units"] })
    },
    onError: (err: Error) => setFormError(err.message),
  })

  // ── Helpers ───────────────────────────────────────────────────────────────

  function openEdit() {
    if (!unit) return
    setForm({
      code:            unit.code,
      name:            unit.name,
      licensePlate:    unit.licensePlate ?? "",
      brand:           unit.brand ?? "",
      manufactureYear: unit.manufactureYear != null ? String(unit.manufactureYear) : "",
      fuelType:        unit.fuelType ?? "",
    })
    setSgtCodes(unit.sgtCodes ?? [])
    setSgtInput("")
    setFormError(null)
    setEditOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!form.code.trim() || !form.name.trim()) {
      setFormError("Código y nombre son requeridos")
      return
    }
    const yearStr = form.manufactureYear.trim()
    const parsedYear = yearStr ? parseInt(yearStr, 10) : null
    if (yearStr && (isNaN(parsedYear!) || parsedYear! < 1900 || parsedYear! > 2100)) {
      setFormError("El año debe estar entre 1900 y 2100")
      return
    }
    updateMut.mutate({
      code:            form.code.trim().toUpperCase(),
      name:            form.name.trim(),
      sgtCodes:        sgtCodes.length ? sgtCodes : null,
      licensePlate:    form.licensePlate.trim().toUpperCase() || null,
      brand:           form.brand.trim() || null,
      manufactureYear: parsedYear,
      fuelType:        (form.fuelType as "diesel" | "gnc" | null) || null,
    })
  }

  // ── Loading / error ───────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <>
        <SiteHeader title="Unidad" />
        <div className="flex flex-1 items-center justify-center py-24 text-sm text-muted-foreground">
          Cargando...
        </div>
      </>
    )
  }

  if (isError || !unit) {
    return (
      <>
        <SiteHeader title="Unidad no encontrada" />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24">
          <p className="text-sm text-muted-foreground">No se encontró la unidad.</p>
          <Button variant="outline" onClick={() => navigate("/units")}>
            <ArrowLeftIcon className="size-4 mr-2" /> Volver a unidades
          </Button>
        </div>
      </>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <SiteHeader title={unit.name} />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-2xl space-y-6">

          {/* Barra superior */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-sm h-8 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <ArrowLeftIcon className="size-4" /> Volver
              </button>
              <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded uppercase tracking-wide">
                {unit.code}
              </span>
              {unit.deletedAt && (
                <Badge className="bg-destructive/10 text-destructive border-destructive/20">
                  Eliminada
                </Badge>
              )}
            </div>

            {can("write", "units") && (
              <div className="flex items-center gap-2">
                {unit.deletedAt ? (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => restoreMut.mutate()}
                    disabled={restoreMut.isPending}
                    className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  >
                    <RestoreIcon className="size-4 mr-1.5" />
                    {restoreMut.isPending ? "Restaurando..." : "Restaurar"}
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={openEdit}>
                      <PencilIcon className="size-4 mr-1.5" /> Editar
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setDeleteOpen(true)}
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                    >
                      <Trash2Icon className="size-4 mr-1.5" /> Eliminar
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Identificación */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Identificación
            </h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Código" value={unit.code} mono />
              <Field label="Nombre" value={unit.name} />
              <div className="col-span-2 flex flex-col gap-0.5">
                <dt className="text-xs text-muted-foreground">Tipo</dt>
                <dd className="text-sm">
                  <span className="bg-muted px-2 py-0.5 rounded text-xs">{unit.unitTypeName}</span>
                </dd>
              </div>
              <Field label="Patente"     value={unit.licensePlate} mono />
              <Field label="Marca"       value={unit.brand} />
              <Field label="VIN"         value={unit.vin} mono />
              <Field label="Motor"       value={unit.engineNumber} mono />
              <Field label="Año"         value={unit.manufactureYear != null ? String(unit.manufactureYear) : null} />
              <Field label="Combustible" value={unit.fuelType === "gnc" ? "GNC" : unit.fuelType === "diesel" ? "Diesel" : null} />
            </dl>
          </div>

          {/* Códigos SGT */}
          <div className="rounded-lg border p-5 space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Códigos SGT (SmartGT)
            </h3>
            {unit.sgtCodes?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {unit.sgtCodes.map((s) => (
                  <span key={s} className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{s}</span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground/50">Sin códigos SGT asignados</p>
            )}
          </div>

          {/* Registro */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Registro
            </h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Creado"      value={fmtDate(unit.createdAt)} />
              <Field label="Actualizado" value={fmtDate(unit.updatedAt)} />
              {unit.deletedAt && (
                <Field label="Eliminada" value={fmtDate(unit.deletedAt)} />
              )}
            </dl>
          </div>

        </div>
      </div>

      {/* Edit Sheet */}
      <Sheet open={editOpen} onOpenChange={(o) => { if (!o) { setEditOpen(false); setFormError(null) } }}>
        <SheetContent side="right" className="sm:max-w-sm overflow-y-auto flex flex-col">
          <SheetHeader>
            <SheetTitle>Editar unidad</SheetTitle>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">

            <div className="grid gap-1.5">
              <Label htmlFor="ud-code">Código</Label>
              <Input
                id="ud-code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                maxLength={20}
                className="font-mono uppercase"
                required
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="ud-name">Nombre</Label>
              <Input
                id="ud-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={200}
                required
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="ud-plate">Patente</Label>
              <Input
                id="ud-plate"
                value={form.licensePlate}
                onChange={(e) => setForm((f) => ({ ...f, licensePlate: e.target.value.toUpperCase() }))}
                maxLength={20}
                className="font-mono uppercase"
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="ud-brand">Marca</Label>
              <Input
                id="ud-brand"
                value={form.brand}
                onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
                maxLength={100}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="ud-year">Año</Label>
              <Input
                id="ud-year"
                type="number"
                value={form.manufactureYear}
                onChange={(e) => setForm((f) => ({ ...f, manufactureYear: e.target.value }))}
                min={1900}
                max={2100}
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Combustible</Label>
              <div className="flex gap-2">
                {([
                  { value: null,     label: "—",     active: "bg-muted text-muted-foreground" },
                  { value: "diesel", label: "🔥 Diesel", active: "bg-orange-100 text-orange-700 border-orange-300" },
                  { value: "gnc",    label: "🌿 GNC",    active: "bg-green-100 text-green-700 border-green-300" },
                ] as const).map((opt) => {
                  const isActive = (form.fuelType || null) === opt.value
                  return (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, fuelType: opt.value ?? "" }))}
                      className={cn(
                        "flex-1 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                        isActive
                          ? opt.active + " border-current"
                          : "border-input bg-background text-muted-foreground hover:bg-muted",
                      )}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Códigos SGT */}
            <div className="grid gap-1.5">
              <Label>Códigos SGT</Label>
              <div className="flex flex-wrap gap-1.5 items-center min-h-9 rounded-md border border-input px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring">
                {sgtCodes.map((code, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-muted font-mono text-xs px-2 py-0.5 rounded-sm">
                    {code}
                    <button
                      type="button"
                      onClick={() => setSgtCodes(sgtCodes.filter((_, j) => j !== i))}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <XIcon className="size-3" />
                    </button>
                  </span>
                ))}
                <input
                  value={sgtInput}
                  onChange={(e) => setSgtInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault()
                      const code = sgtInput.toUpperCase().trim()
                      if (code && !sgtCodes.includes(code)) setSgtCodes([...sgtCodes, code])
                      setSgtInput("")
                    }
                  }}
                  placeholder={sgtCodes.length === 0 ? "Escribir y Enter para agregar..." : ""}
                  className="flex-1 min-w-28 bg-transparent text-xs font-mono outline-none placeholder:text-muted-foreground"
                />
              </div>
              <p className="text-xs text-muted-foreground">Nombres exactos en SmartGT. Presioná Enter o coma para agregar.</p>
            </div>

            {formError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={updateMut.isPending}>
                {updateMut.isPending ? "Guardando..." : "Guardar cambios"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false} className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar unidad</DialogTitle>
            <DialogDescription>
              ¿Eliminás <strong>{unit.code}</strong> — {unit.name}? Esta acción es reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate()}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
